import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, X, Scales, ArrowRight } from "@phosphor-icons/react";
import { api } from "../lib/api";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

const TOOLTIP_STYLE = {
  backgroundColor: "#FFFFFF",
  borderRadius: "0px",
  border: "1px solid #E5E2DC",
  boxShadow: "none",
  color: "#1C1B1A",
  padding: "10px 12px",
  fontFamily: "Manrope, sans-serif",
  fontSize: "12px",
};

const regStatusColor = (s) =>
  s === "compliant"
    ? { fg: "#40593B", bg: "#EDF2EB" }
    : s === "needs_upgrade"
    ? { fg: "#D93636", bg: "#FBEAE9" }
    : { fg: "#F29F05", bg: "#FDF4E6" };

export default function ComparePage() {
  const [allProducts, setAllProducts] = useState([]);
  const [picked, setPicked] = useState([]); // array of product ids
  const [data, setData] = useState([]);
  const [query, setQuery] = useState("");

  useEffect(() => {
    (async () => {
      const rows = await api.listProducts({ limit: 60 });
      setAllProducts(rows);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (picked.length < 2) {
        setData([]);
        return;
      }
      try {
        const d = await api.compareProducts(picked);
        setData(d.items || []);
      } catch {
        setData([]);
      }
    })();
  }, [picked]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return allProducts;
    return allProducts.filter(
      (p) =>
        p.name.toLowerCase().includes(q) || p.brand.toLowerCase().includes(q)
    );
  }, [allProducts, query]);

  const toggle = (id) => {
    setPicked((v) => {
      if (v.includes(id)) return v.filter((x) => x !== id);
      if (v.length >= 4) return v;
      return [...v, id];
    });
  };

  const sentimentBar = useMemo(
    () =>
      data.map((i) => ({
        name: `${i.product.brand}`,
        value: i.stats.avg_sentiment,
      })),
    [data]
  );
  const complaintsBar = useMemo(
    () =>
      data.map((i) => ({
        name: `${i.product.brand}`,
        total: i.stats.complaint_total,
        high: i.stats.high_severity,
      })),
    [data]
  );

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-8 lg:py-12" data-testid="compare-page">
      <div className="eyebrow flex items-center gap-1.5">
        <Scales size={12} /> Side-by-side
      </div>
      <h1 className="font-heading font-light text-4xl sm:text-5xl tracking-tight mt-2">
        Compare <span className="font-accent italic text-[#A66E4E]">products</span>
      </h1>
      <p className="text-[#6E6B65] mt-3 max-w-2xl">
        Pick 2 to 4 products to compare sentiment, complaint volume, channels and regulatory
        status across regions.
      </p>

      {/* Picker */}
      <div className="mt-8 border border-[#E5E2DC] bg-white">
        <div className="border-b border-[#E5E2DC] p-4 flex flex-wrap items-center gap-3">
          <input
            data-testid="compare-search"
            placeholder="Filter products…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="px-3 py-2 border border-[#E5E2DC] focus:border-[#A66E4E] focus:outline-none focus:ring-1 focus:ring-[#A66E4E] bg-white text-sm flex-1 min-w-[200px] font-heading"
          />
          <div className="eyebrow ml-auto">{picked.length}/4 selected</div>
        </div>
        <div className="max-h-[260px] overflow-y-auto thin-scroll grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E5E2DC]">
          {filtered.map((p) => {
            const active = picked.includes(p.id);
            return (
              <button
                key={p.id}
                type="button"
                data-testid="compare-pick"
                onClick={() => toggle(p.id)}
                className={`text-left bg-white p-3 flex items-center gap-3 transition-colors ${
                  active ? "outline outline-2 outline-[#A66E4E] -outline-offset-2" : "hover:bg-[#EFECE6]"
                }`}
              >
                <div className="w-10 h-10 bg-[#EFECE6] overflow-hidden shrink-0">
                  {p.image_url && <img src={p.image_url} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="eyebrow text-[9px]">{p.brand}</div>
                  <div className="font-heading text-sm truncate">{p.name}</div>
                </div>
                {active ? (
                  <X size={14} className="text-[#D93636]" />
                ) : (
                  <Plus size={14} className="text-[#A66E4E]" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Comparison */}
      {data.length >= 2 && (
        <section className="mt-10 space-y-10" data-testid="compare-results">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#E5E2DC] border border-[#E5E2DC]">
            {data.map((i) => (
              <Link
                to={`/product/${i.product.id}`}
                key={i.product.id}
                className="bg-white p-5 flex flex-col gap-2 hover:bg-[#EFECE6]/40 transition-colors"
                data-testid="compare-card"
              >
                <div className="aspect-[4/3] bg-[#EFECE6] overflow-hidden">
                  {i.product.image_url && (
                    <img src={i.product.image_url} alt="" className="w-full h-full object-cover" />
                  )}
                </div>
                <div className="eyebrow">{i.product.brand}</div>
                <div className="font-accent italic text-xl leading-tight">{i.product.name}</div>
                <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
                  <div>
                    <div className="eyebrow text-[8px]">signals</div>
                    <div className="font-heading text-lg tabular-nums">{i.stats.complaint_total}</div>
                  </div>
                  <div>
                    <div className="eyebrow text-[8px]">avg sent.</div>
                    <div
                      className="font-heading text-lg tabular-nums"
                      style={{
                        color:
                          i.stats.avg_sentiment >= 0.2
                            ? "#40593B"
                            : i.stats.avg_sentiment <= -0.2
                            ? "#D93636"
                            : "#1C1B1A",
                      }}
                    >
                      {i.stats.avg_sentiment.toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <div className="eyebrow text-[8px]">sellers</div>
                    <div className="font-heading text-lg tabular-nums">{i.stats.sellers_count}</div>
                  </div>
                  <div>
                    <div className="eyebrow text-[8px]">high-sev</div>
                    <div className="font-heading text-lg tabular-nums text-[#D93636]">
                      {i.stats.high_severity}
                    </div>
                  </div>
                </div>
                <span className="mt-auto text-[11px] uppercase tracking-wider font-semibold text-[#A66E4E] flex items-center gap-1">
                  inspect <ArrowRight size={12} weight="bold" />
                </span>
              </Link>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 grid-borders border border-[#E5E2DC]">
            <div className="p-6 md:p-8">
              <div className="eyebrow">Average sentiment</div>
              <h3 className="font-heading text-xl font-medium mt-1 mb-4">Consumer tone</h3>
              <div className="w-full h-[240px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={sentimentBar} margin={{ top: 5, right: 16, left: -4, bottom: 0 }}>
                    <CartesianGrid stroke="#E5E2DC" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: "#6E6B65", fontSize: 11 }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: "#6E6B65", fontSize: 11 }} domain={[-1, 1]} />
                    <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: "#EFECE6" }} />
                    <Bar dataKey="value">
                      {sentimentBar.map((d, i) => (
                        <Cell key={i} fill={d.value < 0 ? "#D93636" : "#40593B"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="p-6 md:p-8">
              <div className="eyebrow">Complaint volume</div>
              <h3 className="font-heading text-xl font-medium mt-1 mb-4">Total signals · high-severity</h3>
              <div className="w-full h-[240px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={complaintsBar} margin={{ top: 5, right: 16, left: -4, bottom: 0 }}>
                    <CartesianGrid stroke="#E5E2DC" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: "#6E6B65", fontSize: 11 }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: "#6E6B65", fontSize: 11 }} />
                    <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: "#EFECE6" }} />
                    <Bar dataKey="total" fill="#A66E4E" />
                    <Bar dataKey="high" fill="#D93636" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Regulatory matrix */}
          <div className="border border-[#E5E2DC] bg-white overflow-x-auto">
            <div className="p-6 md:p-8 border-b border-[#E5E2DC]">
              <div className="eyebrow">Regulatory matrix</div>
              <h3 className="font-heading text-xl font-medium mt-1">Per-region compliance</h3>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-[#1C1B1A] text-left">
                  <th className="px-4 py-3 text-[10px] tracking-[0.18em] uppercase font-semibold text-[#6E6B65]">Product</th>
                  {["EU", "USA", "UK", "India", "GCC", "China", "South Korea"].map((r) => (
                    <th key={r} className="px-3 py-3 text-[10px] tracking-[0.18em] uppercase font-semibold text-[#6E6B65]">
                      {r}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((i) => (
                  <tr key={i.product.id} className="border-b border-[#E5E2DC]">
                    <td className="px-4 py-3">
                      <div className="eyebrow text-[9px]">{i.product.brand}</div>
                      <div className="font-heading text-sm">{i.product.name}</div>
                    </td>
                    {["EU", "USA", "UK", "India", "GCC", "China", "South Korea"].map((r) => {
                      const s = i.product.regulatory_status?.[r] || "compliant";
                      const col = regStatusColor(s);
                      return (
                        <td key={r} className="px-3 py-3">
                          <span
                            className="text-[10px] uppercase tracking-[0.14em] font-semibold px-1.5 py-0.5 inline-block"
                            style={{ background: col.bg, color: col.fg }}
                          >
                            {s.replace("_", " ")}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {data.length < 2 && picked.length < 2 && (
        <div className="mt-10 border border-dashed border-[#E5E2DC] bg-white p-10 text-center text-[#6E6B65]">
          Pick at least 2 products to compare.
        </div>
      )}
    </div>
  );
}
